import { TListingRoadblockConfiguration } from "../listingRoadblockTypesAndCodecs";

// Opens when an offer is accepted and resolves once the sale is safe to hand to the
// conveyancers: the buyer is proven proceedable, and both sides know the sale exists.
//
// The trigger is already wired and needs nothing here. Accepting or un-accepting an offer
// moves `listings.accepted_listings_enquiries_offer_id` via the trigger on
// `listings_enquiries_offers`, and `patchOffer` refreshes the listing's roadblocks off the
// back of it — so this roadblock opens on accept and suspends if the offer falls through,
// without a status movement of its own.
//
// Scoped deliberately to buyer proceedability and the two kick-off emails, which is a
// fortnight's work with a deadline that means something. The progression milestones that
// follow — contracts, survey, the mortgage chain, enquiries, exchange and completion dates —
// belong to Completed, which opens on the same trigger and runs the length of the
// transaction. The two roadblocks run in parallel and their SLAs mean different things.
//
// Every item reads the accepted buyer's enquiry rather than the listing. Those facts arrive
// through `SupplementaryListingRoadblockData` because they live on `listings_enquiries`, two
// joins from the listing row.
export const UnderOffer: TListingRoadblockConfiguration = {
    type: "under_offer",
    display_name: "Under offer",
    // Not gated on Market Prep Ready. An offer can be accepted while prep items are still
    // outstanding — a listing that goes under offer off a single viewing is common — and
    // making this wait would hide the buyer checks for exactly the sales moving fastest.
    only_create_if_resolved: [],
    create: {
        create_condition_description: "An offer has been accepted on the listing",
        predicate: (d) => d.listing.accepted_listings_enquiries_offer_id !== null,
    },
    checklist: [
        {
            type: "buyer_id_check_complete",
            is_resolved_by_short_description: "Buyer ID check",
            resolved_by_party: "buyer",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The accepted buyer has completed their ID check",
                predicate: (d) => d.buyer_id_check_complete,
            },
        },
        {
            type: "buyer_funds_verified",
            is_resolved_by_short_description: "Proof of funds",
            resolved_by_party: "buyer",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The buyer's funds are evidenced by verified cash, a DIP, or a broker vouching for them",
                predicate: (d) => d.buyer_funds_verified,
            },
        },
        {
            type: "buyer_conveyancer_captured",
            is_resolved_by_short_description: "Buyer conveyancer details",
            resolved_by_party: "buyer",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The buyer's conveyancer has been named with an email address to write to",
                predicate: (d) => d.buyer_conveyancer_captured,
            },
        },
        {
            type: "seller_solicitor_kicked_off",
            is_resolved_by_short_description: "Seller solicitor kicked off",
            resolved_by_party: "us",
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The opening email has gone to the seller's solicitor",
                predicate: (d) => d.listing.seller_solicitor_kick_off_email_sent !== null,
            },
        },
        {
            type: "buyer_solicitor_kicked_off",
            is_resolved_by_short_description: "Buyer solicitor kicked off",
            resolved_by_party: "us",
            // Last in declaration order because it is the one that needs the item above it
            // done first: there is nobody to write to until the buyer's conveyancer has been
            // captured. The panel's "next item" follows this order, so it reads as the
            // sequence the work actually happens in.
            create: {
                create_condition_description: "Always",
                predicate: () => true,
            },
            resolve: {
                is_resolved_by_long_description: "The opening email has gone to the buyer's conveyancer",
                predicate: (d) => d.listing.buyer_solicitor_kick_off_email_sent !== null,
            },
        },
    ],
};
